import { AnimatePresence, motion } from 'framer-motion';
import { Link, Plus, Smartphone } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  APK_EXTENSION_ERROR_MESSAGE,
  hasApkFileExtension,
} from '../../utils/apkValidation';
import { formatFileSize } from '../../utils/stringHelpers';
import type { PublicationSource } from '../../utils/publicationWorkflow';
import { Button } from '../Button';
import { FieldLabel } from '../Form';
import { INPUT_CLASSES } from '../Input';
import { EASE_OUT, fadeIn, PortalCard } from '../portal';
import { TextArea } from '../TextArea';
import type {
  ProcessingStatus,
  PublicationDraft,
  PublicationPhase,
  UploadState,
} from './types';

interface VersionUploadContainerProps {
  dappName: string;
  onSubmit: (draft: PublicationDraft) => void;
  uploadState: UploadState;
  status?: ProcessingStatus;
  errorMessage?: string;
  onClearError: () => void;
  onValidityChange: (isValid: boolean) => void;
  onTouchedChange: (isTouched: boolean) => void;
  isSubmitting: boolean;
  isRepairingPackage?: boolean;
  onRepairPackageCase?: (repair: PackageCaseRepair) => Promise<void> | void;
}

export interface PackageCaseRepair {
  currentPackageName: string;
  newPackageName: string;
}

const PACKAGE_NAME_PATTERN =
  '[A-Za-z][A-Za-z0-9_]*(?:\\.[A-Za-z][A-Za-z0-9_]*)+';
const PACKAGE_MISMATCH_PATTERN = new RegExp(
  `Package name mismatch\\. Expected: (${PACKAGE_NAME_PATTERN}), Got: (${PACKAGE_NAME_PATTERN})\\.`,
);

export function getPackageCaseRepair(
  errorMessage?: string,
): PackageCaseRepair | null {
  const match = errorMessage?.match(PACKAGE_MISMATCH_PATTERN);
  if (!match || match[1] === match[2]) {
    return null;
  }

  return match[1].toLowerCase() === match[2].toLowerCase()
    ? { currentPackageName: match[1], newPackageName: match[2] }
    : null;
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

const PHASE_LABELS: Record<PublicationPhase, string> = {
  preparing: 'Processing APK',
  uploading: 'Processing APK',
  queued: 'Processing APK',
  processing: 'Processing APK',
  minting: 'Minting Release NFT',
  verifying: 'Verifying Release NFT',
  submitting: 'Submitting for Review',
  done: 'Release Submitted',
};

/** form → uploading → error: 100ms fade out, 150ms fade in (`mode="wait"`). */
const PHASE_FADE = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.15, ease: EASE_OUT } },
  exit: { opacity: 0, transition: { duration: 0.1, ease: EASE_OUT } },
};

/** APK drop zone ⇄ URL input: height + fade, 180ms in / 150ms out. */
const SOURCE_PANEL = {
  initial: { opacity: 0, height: 0 },
  animate: {
    opacity: 1,
    height: 'auto' as const,
    transition: { duration: 0.18, ease: EASE_OUT },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.15, ease: EASE_OUT },
  },
};

function PageTitle({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div
        role="heading"
        aria-level={1}
        className="text-4xl font-medium leading-10 text-white"
      >
        {title}
      </div>
      <p className="text-sm leading-5 text-seeker-400">{description}</p>
    </div>
  );
}

const SOURCE_TOGGLE_CLASSES =
  'shrink-0 rounded text-sm font-medium text-white underline-offset-4 transition-colors duration-150 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-seeker-900';

export function VersionUploadContainer({
  dappName,
  onSubmit,
  uploadState,
  status,
  errorMessage,
  onClearError,
  onValidityChange,
  onTouchedChange,
  isSubmitting,
  isRepairingPackage = false,
  onRepairPackageCase,
}: VersionUploadContainerProps) {
  const [sourceKind, setSourceKind] =
    useState<PublicationSource['kind']>('portalUpload');
  const [file, setFile] = useState<File | null>(null);
  const [apkUrl, setApkUrl] = useState('');
  const [whatsNew, setWhatsNew] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const isValid =
      whatsNew.trim().length > 0 &&
      (sourceKind === 'portalUpload'
        ? !!file
        : isHttpsUrl(apkUrl.trim()) && apkUrl.trim().length > 0);

    onValidityChange(isValid);
  }, [apkUrl, file, onValidityChange, sourceKind, whatsNew]);

  const setTouched = () => {
    onTouchedChange(true);
  };

  const handleFileSelect = (selectedFile: File) => {
    if (!hasApkFileExtension(selectedFile.name)) {
      toast.error(APK_EXTENSION_ERROR_MESSAGE);
      return;
    }

    setFile(selectedFile);
    setTouched();
    if (sourceKind === 'externalUrl') {
      setSourceKind('portalUpload');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const submitDraft = (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || uploadState !== 'form') {
      return;
    }

    if (sourceKind === 'portalUpload' && file) {
      onSubmit({
        source: { kind: 'portalUpload', file },
        whatsNew,
      });
      return;
    }

    if (sourceKind === 'externalUrl' && isHttpsUrl(apkUrl.trim())) {
      onSubmit({
        source: { kind: 'externalUrl', url: apkUrl.trim() },
        whatsNew,
      });
    }
  };

  const renderError = () => {
    const packageCaseRepair = getPackageCaseRepair(errorMessage);

    const repairPackageCase = async () => {
      if (
        !packageCaseRepair ||
        !onRepairPackageCase ||
        !window.confirm(
          `Change this Draft app and its App NFT package from ${packageCaseRepair.currentPackageName} to ${packageCaseRepair.newPackageName}? This is only allowed before any release exists.`,
        )
      ) {
        return;
      }

      await onRepairPackageCase(packageCaseRepair);
    };

    return (
      <>
        <PageTitle
          title="Upload Failed"
          description="There was an error uploading your release."
        />
        <PortalCard className="border-red-500/40 p-6">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 text-left">
              <div className="text-sm font-bold text-red-400">
                Publication failed
              </div>
              <p className="mt-2 break-words text-sm text-seeker-400">
                {errorMessage}
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-2">
              {packageCaseRepair && onRepairPackageCase && (
                <Button
                  variant="dangerOutline"
                  onClick={repairPackageCase}
                  disabled={isRepairingPackage}
                >
                  {isRepairingPackage ? 'Repairing…' : 'Use APK package'}
                </Button>
              )}
              <Button
                variant="outlineMuted"
                onClick={onClearError}
                disabled={isRepairingPackage}
              >
                Reset
              </Button>
            </div>
          </div>
        </PortalCard>
      </>
    );
  };

  const renderUploading = () => {
    const progress = Math.min(100, Math.max(0, status?.progress || 0));

    return (
      <>
        <PageTitle
          title="Uploading..."
          description={`Uploading a new release for ${dappName || 'your app'}.`}
        />
        <div className="flex flex-col gap-3">
          <FieldLabel
            label={
              status?.phase ? PHASE_LABELS[status.phase] : 'Publishing version'
            }
            explanation={status?.message}
          />
          <PortalCard
            role="status"
            aria-live="polite"
            className="flex flex-col items-center gap-2 px-16 py-10"
          >
            <span className="text-sm text-seeker-400 tabular-nums">
              {Math.round(progress)}%
            </span>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-seeker-700">
              <div
                className="h-full rounded-full bg-seeker-green transition-[width] duration-200 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            {status?.ingestionSessionId && (
              <p className="mt-2 text-xs text-seeker-500">
                Session{' '}
                <span className="text-seeker-400">
                  {status.ingestionSessionId}
                </span>
              </p>
            )}
          </PortalCard>
        </div>
      </>
    );
  };

  const renderForm = () => (
    <>
      <PageTitle
        title="Upload your dApp Release"
        description="Add your upcoming release for the Solana Mobile team to review."
      />

      <div className="flex flex-col gap-2">
        <div className="relative flex items-end justify-between gap-4">
          <FieldLabel
            label="dApp File"
            explanation={
              sourceKind === 'portalUpload'
                ? 'Upload your Android app file in .APK format.'
                : 'Provide a stable HTTPS link to your APK.'
            }
          />
          <AnimatePresence mode="popLayout" initial={false}>
            {sourceKind === 'portalUpload' ? (
              <motion.button
                key="use-url"
                type="button"
                {...fadeIn}
                onClick={() => {
                  setSourceKind('externalUrl');
                  setTouched();
                }}
                className={SOURCE_TOGGLE_CLASSES}
              >
                or use APK URL
              </motion.button>
            ) : (
              <motion.button
                key="use-upload"
                type="button"
                {...fadeIn}
                onClick={() => {
                  setSourceKind('portalUpload');
                  setTouched();
                }}
                className={SOURCE_TOGGLE_CLASSES}
              >
                or upload APK
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        <div className="relative">
          <AnimatePresence mode="wait" initial={false}>
            {sourceKind === 'externalUrl' ? (
              <motion.div key="url-input" {...SOURCE_PANEL}>
                <div className="relative">
                  <Link className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-seeker-400" />
                  <input
                    type="url"
                    aria-label="APK URL"
                    value={apkUrl}
                    onChange={(event) => {
                      setApkUrl(event.target.value);
                      setTouched();
                    }}
                    className={`${INPUT_CLASSES} pl-11`}
                    placeholder="https://example.com/app-release.apk"
                    disabled={isSubmitting}
                  />
                </div>
                <p className="mt-2 text-xs leading-4 text-seeker-400">
                  The portal will download, validate, and ingest the APK from
                  this URL.
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="upload-area"
                {...SOURCE_PANEL}
                role="region"
                aria-label="File upload area"
                className={cn(
                  'relative rounded-lg border border-dashed transition-colors duration-150',
                  isDragging
                    ? 'border-white bg-white/5'
                    : 'border-seeker-700 hover:border-seeker-500',
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".apk"
                  aria-label="APK file"
                  onChange={handleFileChange}
                  className="sr-only"
                  disabled={isSubmitting}
                />

                {file ? (
                  <div className="flex min-h-[114px] items-center gap-4 px-6 py-5">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-seeker-800">
                      <Smartphone className="size-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate font-mono text-sm text-white"
                        title={file.name}
                      >
                        {file.name}
                      </p>
                      <p className="mt-1 text-xs leading-4 text-seeker-400 tabular-nums">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <Button
                      variant="outlineMuted"
                      className="h-8 px-3"
                      onClick={() => setFile(null)}
                      disabled={isSubmitting}
                    >
                      Replace
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex h-[114px] w-full flex-col items-center justify-center gap-3 rounded-lg text-white transition-colors duration-150 hover:bg-white/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70 disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    <Plus className="size-8" strokeWidth={1.5} />
                    <span className="text-xs leading-4">.APK format</span>
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <FieldLabel
          label={<label htmlFor="whats-new">What&apos;s New</label>}
          required
          explanation="Tell users what's new in this version."
        />
        <TextArea
          id="whats-new"
          aria-label="What's New"
          value={whatsNew}
          onChange={(event) => {
            setWhatsNew(event.target.value);
            setTouched();
          }}
          rows={4}
          placeholder={
            '• New features and improvements\n• Performance enhancements\n• UI updates'
          }
          disabled={isSubmitting}
        />
      </div>
    </>
  );

  // One form per phase, keyed so the phases crossfade instead of snapping.
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.form
        key={uploadState}
        id="version-form"
        onSubmit={submitDraft}
        className="space-y-10"
        {...PHASE_FADE}
      >
        {uploadState === 'error'
          ? renderError()
          : uploadState === 'uploading'
            ? renderUploading()
            : renderForm()}
      </motion.form>
    </AnimatePresence>
  );
}
